import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;

/**
 * A custom element that manages a header menu using click instead of hover.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Attach click event listener to handle opening/closing
    this.addEventListener('click', this.#clickHandler.bind(this), {
      signal: this.#abortController.signal,
    });

    // Close on outside click
    document.addEventListener('click', this.#outsideClickHandler);

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    document.removeEventListener('click', this.#outsideClickHandler);
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Handle click events on the menu to toggle open/close
   * @param {MouseEvent} event
   */
  #clickHandler(event) {
    const item = findMenuItem(event.target);
    if (!item) return;

    const submenu = findSubmenu(item);

    // Only prevent default if the menu item has a submenu
    if (submenu) {
      event.preventDefault();
      event.stopPropagation();

      const isAlreadyActive = item === this.#state.activeItem;

      // Close current active
      if (this.#state.activeItem) {
        this.#deactivate(this.#state.activeItem);
      }

      // Activate only if clicking a different one
      if (!isAlreadyActive) {
        this.#activateHandler(event);
      }
    }
    // Else: no submenu, allow normal navigation
  }

  /**
   * Handle clicks outside the menu to close it
   * @param {MouseEvent} event
   */
  #outsideClickHandler = (event) => {
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu.contains(event.target)) {
      headerMenu?.publicDeactivate();
    }
  };

  /**
   * Activates the selected menu item
   * @param {PointerEvent | MouseEvent} event
   */
  #activateHandler = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  /**
   * Deactivate the active item
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  // Expose deactivate method for outside click
  publicDeactivate() {
    this.#deactivate();
  }

  /**
   * Preload images that are set to load lazily
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.closest('[ref="menuitem"]');
}

/**
 * Find the closest submenu
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}


/* TM Code Start */
document.addEventListener("DOMContentLoaded", function () {
  const menuItems = document.querySelectorAll(".menu-list__list-item");

  menuItems.forEach((item) => {
    const submenu = item.querySelector(".menu-list__submenu");
    const link = item.querySelector(".menu-list__link");

    if (!submenu || !link) return;

    link.addEventListener("click", function (e) {
      e.preventDefault();

      // close other open menus
      menuItems.forEach((otherItem) => {
        if (otherItem !== item) {
          otherItem.classList.remove("active");
        }
      });

      // toggle current
      item.classList.toggle("active");
    });
  });

  // close when clicking outside
  document.addEventListener("click", function (e) {
    if (!e.target.closest(".header-menu")) {
      menuItems.forEach((item) => {
        item.classList.remove("active");
      });
    }
  });
});



/* TM Code End */